import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import InternalOnlyBadge, { InlineInternalOnlyBadge } from '@orn/shared-web/components/InternalOnlyBadge';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { BarChartIcon, BankNoteIcon, CalendarIcon, HomeIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useFeatureGate } from '../../hooks/useFeatureGates';
import { PATHS } from '../../paths';
import { useAuthState } from '../../providers/AuthProvider';
import { usePathWithDateRange } from '../../providers/DateRangeProvider';
import EntitySearchModal from '../search/EntitySearchModal';

import { MAIN_SIDEBAR_WIDTH_PX } from './dimensions';
import FullSidebarContent from './FullSidebarContent';
import IconRailSidebar from './IconRailSidebar';
import MobileNavDrawer from './MobileNavDrawer';
import MobileTopBar from './MobileTopBar';

import type { NavItemType } from './config';
import type { LoggedInUser } from '../../providers/AuthProvider';

interface IBaseSidebarProps {
  user: LoggedInUser;
  /** URL of the currently active item. */
  activeUrl?: string;
  /** List of items to display. */
  items: NavItemType[];
  /** List of footer items to display. */
  footerItems?: NavItemType[];
  /** Whether to show the account card. */
  showAccountCard?: boolean;
  /**
   * Whether to render the "Mantle Assistant" footer pill (full sidebar +
   * mobile drawer) and the compact mark button (icon rail). Drives
   * visibility across all three sidebar presentations together.
   */
  showAssistantButton?: boolean;
  /** Additional CSS classes to apply to the desktop fixed sidebar. */
  className?: string;
}

/**
 * Responsive navigation chrome for the logged-in app. Renders three
 * presentations driven by Tailwind visibility classes (no viewport JS):
 *
 * - `lg` and up: 260px fixed sidebar on the left (full content).
 * - `md`: 64px icon-only rail on the left.
 * - below `md`: sidebar hidden; fixed top bar hosts a hamburger that opens a
 *   left-slide drawer containing the full sidebar content.
 *
 * Owns search-modal (Cmd/Ctrl+K) and drawer open state; closes the drawer on
 * route change.
 *
 * **Why CSS visibility instead of `useBreakpoint` JS gating?**
 * All three trees mount at every viewport and Tailwind's `hidden md:flex
 * lg:hidden`-style classes hide the inactive ones. Costs ~30–40 hidden DOM
 * nodes (`display:none`, not painted, not in layout) — well below anything
 * worth optimizing. In return:
 * - State is preserved across resize for most subtrees (e.g. the search
 *   modal). JS gating would unmount the inactive variants and require
 *   `useEffect`s to reconcile stale state against unmounted views.
 * - First paint is correct without a window-size read. There's no SSR in
 *   this app, so flash isn't the threat — the savings are robustness, not
 *   pixels.
 * If we ever embed something expensive (charts, virtualized lists) inside
 * one of the variants, revisit and gate that subtree on `useBreakpoint`.
 * The shell stays cheap.
 *
 * **Drawer is the one exception** to "state preserved across resize":
 * `MobileNavDrawer` is `lg:hidden` (visible at both `<md` and `md`) — the
 * mobile top bar's hamburger opens it at `<md`, and the icon rail's top
 * hamburger opens it at `md`. At `lg+` the drawer's overlay is
 * CSS-invisible but `react-aria-components`' `Modal` still mounts the
 * dialog and activates a focus trap when `isOpen` is true. The hamburgers
 * that open it are both also gone at `lg+` so a fresh open at `lg+` is
 * impossible, but stale `isDrawerOpen=true` across the resize boundary
 * is not. We watch the `lg` breakpoint and force-close when it crosses
 * true.
 */
export const BaseSidebar = ({
  user,
  activeUrl,
  items,
  footerItems = [],
  showAccountCard = true,
  showAssistantButton = false,
  className,
}: IBaseSidebarProps) => {
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const isLgUp = useBreakpoint('lg');

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  React.useEffect(() => {
    setIsDrawerOpen(false);
  }, [activeUrl]);

  // Defensive: close the drawer if the viewport crosses to `lg+` while it's
  // open. The overlay is `lg:hidden` (CSS-invisible at `lg+`) but the
  // underlying react-aria modal would still keyboard-trap focus. See JSDoc
  // above.
  React.useEffect(() => {
    if (isLgUp) {
      setIsDrawerOpen(false);
    }
  }, [isLgUp]);

  const openSearch = React.useCallback(() => setIsSearchOpen(true), []);
  const openDrawer = React.useCallback(() => setIsDrawerOpen(true), []);

  return (
    <>
      {/* Desktop fixed sidebar (lg+) */}
      <div
        style={{ '--width': `${MAIN_SIDEBAR_WIDTH_PX}px` } as React.CSSProperties}
        className={cx('lg:w-(--width) bg-app fixed inset-y-0 left-0 z-30 hidden max-w-full lg:flex', className)}
      >
        <FullSidebarContent
          user={user}
          activeUrl={activeUrl}
          items={items}
          footerItems={footerItems}
          showAccountCard={showAccountCard}
          showAssistantButton={showAssistantButton}
          onOpenSearch={openSearch}
        />
      </div>

      {/* Tablet icon rail (md only) */}
      <IconRailSidebar
        user={user}
        activeUrl={activeUrl}
        items={items}
        onOpenDrawer={openDrawer}
        showAssistantButton={showAssistantButton}
        className="bg-app fixed inset-y-0 left-0 z-30 hidden md:flex lg:hidden"
      />

      {/* Mobile (<md) top bar + drawer */}
      <MobileTopBar onOpenDrawer={openDrawer} className="md:hidden" />
      <MobileNavDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        user={user}
        activeUrl={activeUrl}
        items={items}
        footerItems={footerItems}
        showAccountCard={showAccountCard}
        showAssistantButton={showAssistantButton}
        onOpenSearch={openSearch}
      />

      <EntitySearchModal isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </>
  );
};

/**
 * Wraps `CalendarIcon` with an `InternalOnlyBadge` overlay. Used only by
 * the 64px icon rail variant of the "Bills & Subscriptions" sidebar
 * item — at rail width the inline pip would crowd the small label,
 * so the badge sits on the icon instead. The full sidebar / mobile
 * drawer variant uses plain `CalendarIcon` plus an
 * `InlineInternalOnlyBadge` after the label (see `labelSuffix` below).
 *
 * NavItem injects sizing/margin/color classes (`mr-2 size-5 text-fg-*`)
 * onto the icon component it receives. We apply those to the wrapper span
 * (so `mr-2` separates the whole icon-plus-badge group from the label,
 * and the badge's `-right-0.5` is anchored to the wrapper's edge — i.e.
 * the icon's edge — rather than 8px past it). The inner `CalendarIcon`
 * fills the wrapper via `size-full` and inherits color via `currentColor`.
 */
const BillsSubscriptionsIconRailIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span className={cx('relative inline-flex', className)}>
    <CalendarIcon className="size-full" aria-hidden="true" />
    <InternalOnlyBadge />
  </span>
);

/**
 * App-wired `MainSidebar` used by `HomeScene`. Pulls the authenticated user
 * and active route from context and composes the nav item list (dashboard,
 * accounts, spending, recurring) before delegating to `BaseSidebar`.
 *
 * Two consumer features ride on internal-only feature gates:
 * - "Bills & Subscriptions" entry — `feat-obligations`. Full sidebar /
 *   mobile drawer render plain `CalendarIcon` + `InlineInternalOnlyBadge`
 *   after the label; icon rail overlays `InternalOnlyBadge` on the icon
 *   via `iconRailIcon`.
 * - "Mantle Assistant" footer button — `feat-assistant`. Rendered by each
 *   sidebar variant via `MantleAssistantNavButton`; the gate flag flows
 *   down through `BaseSidebar`. Internal-only annotation lives inline
 *   after the label in the full pill and overlaid on the mark in the
 *   compact icon-rail variant.
 *
 * Internal debug utilities (Sentry test, Environment) live in the avatar
 * dropdown's `Debug` submenu gated on `debug-tools` ([ORN-691]).
 */
const MainSidebar = () => {
  const { user, loading } = useAuthState();
  const location = useLocation();
  const addDateParamsToPath = usePathWithDateRange();
  const activeUrl = location.pathname;
  const showBillsSubscriptions = useFeatureGate(FeatureGate.Obligations);
  const showAssistantButton = useFeatureGate(FeatureGate.Assistant);

  if (loading) {
    return null;
  }

  if (!user) {
    return null;
  }

  const rawItems: NavItemType[] = [
    {
      label: 'Home',
      href: PATHS.DASHBOARD,
      icon: HomeIcon,
    },
    {
      label: 'Accounts',
      href: PATHS.ACCOUNTS,
      icon: BarChartIcon,
    },
    {
      label: 'Spending',
      href: PATHS.SPENDING(),
      icon: BankNoteIcon,
    },
    ...(showBillsSubscriptions
      ? [
          {
            label: 'Bills & Subscriptions',
            // The full label overflows the 64px icon rail and gets clipped
            // off-screen; the rail uses this shorter form instead. Full
            // sidebar + mobile drawer continue to show the long label.
            iconRailLabel: 'Bills',
            href: PATHS.OBLIGATIONS,
            // Full sidebar + mobile drawer: plain icon, with the
            // internal-only annotation rendered as an inline pip
            // after the label via `labelSuffix`.
            icon: CalendarIcon,
            // Icon rail: badge overlaid on the icon since the rail's
            // small label sits below the icon and a trailing pip
            // would crowd it.
            iconRailIcon: BillsSubscriptionsIconRailIcon,
            labelSuffix: <InlineInternalOnlyBadge />,
          },
        ]
      : []),
    {
      label: 'Recurring',
      href: PATHS.RECURRING_TRANSACTIONS,
      icon: CalendarIcon,
    },
  ];

  const items: NavItemType[] = rawItems.map((item) => {
    if (item.items?.length) {
      return {
        ...item,
        href: item.href ? addDateParamsToPath(item.href) : undefined,
        items: item.items.map((subItem) => ({
          ...subItem,
          href: addDateParamsToPath(subItem.href),
        })),
      };
    }
    return {
      ...item,
      href: item.href ? addDateParamsToPath(item.href) : undefined,
    };
  });

  return <BaseSidebar items={items} user={user} activeUrl={activeUrl} showAssistantButton={showAssistantButton} />;
};

export default MainSidebar;
